/** A heading or title from the CMS is plain text unless it carries a tag or an entity; plain text renders as text. */
export function isInlineHtml(value: unknown): boolean {
  return (
    typeof value === "string" &&
    /<\/?[a-z][a-z\d]*\b[^>]*>|&(?:#\d+|#x[\da-f]+|[a-z][a-z\d]*);/i.test(value)
  );
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  shy: "",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  laquo: "«",
  raquo: "»",
};

function decodeEntities(text: string): string {
  return text.replace(
    /&(#x[\da-f]+|#\d+|[a-z][a-z\d]*);/gi,
    (match, entity: string) => {
      if (entity[0] !== "#") return ENTITIES[entity.toLowerCase()] ?? match;
      const code =
        entity[1] === "x" || entity[1] === "X"
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    },
  );
}

/** For the page title, anchors, labels and the one-H1 check: marks dropped, line breaks as spaces. */
export function inlinePlainText(value: unknown): string {
  if (typeof value !== "string") return "";
  if (!isInlineHtml(value)) return value.trim();
  return decodeEntities(
    value
      .replace(/<(script|style)\b[\s\S]*?(?:<\/\1\s*>|$)/gi, " ")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/?(?:p|div|h[1-6]|li)\b[^>]*>/gi, " ")
      .replace(/<!--[\s\S]*?(?:-->|$)|<\/?[a-z][a-z\d-]*\b[^>]*>/gi, ""),
  )
    .replace(/\s+/g, " ")
    .trim();
}

/** A length limit counts the visible text: formatted text within it stays whole, longer text loses its marks. */
export function limitInlineText(value: string, limit: number): string {
  if (!isInlineHtml(value)) return value.slice(0, limit);
  const plain = inlinePlainText(value);
  return plain.length <= limit ? value : plain.slice(0, limit);
}
